import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { nomeDoSistema } from "../sistemas";
import {
  IconList,
  IconCalendar,
  IconClock,
  IconRefresh,
  IconShield,
  IconAlert,
} from "../icons";
import ErrorState from "./ErrorState";

/**
 * PAINEL — a tela que abre o sistema.
 *
 * Responde "o que precisa de mim agora?": a fila de pedidos, as próximas
 * reuniões, os lembretes vencendo, o que foi entregue por último e como está a
 * suíte (auditoria das últimas 24h). Tudo vem de uma chamada só (/api/painel).
 */

interface Painel {
  solicitacoes: {
    abertas: number;
    urgentes: number;
    hoje: number;
    atrasadas: number;
    concluidas_7d: number;
  };
  fila: any[];
  reunioes: any[];
  pedidosReuniao?: any[];
  lembretes: any[];
  atualizacoes: any[];
  auditoria: { eventos_24h: number; falhas_24h: number; sistemas_24h: number };
  avisosNoAr: any[];
  email: { configurado: boolean };
}

export function dataBr(v: string | null | undefined): string {
  if (!v) return "—";
  const iso = String(v).slice(0, 10);
  const [a, m, d] = iso.split("-");
  return d ? d + "/" + m + "/" + a : String(v);
}

export default function PainelView({
  irPara,
  onMudou,
}: {
  irPara: (aba: string) => void;
  onMudou: () => void;
}) {
  const [dados, setDados] = useState<Painel | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setDados(await api.painel());
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível carregar o painel.");
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function concluirLembrete(id: number) {
    try {
      await api.lembretes.atualizar(id, { status: "Concluído" });
      await carregar();
      onMudou();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erro ao concluir.");
    }
  }

  if (erro) return <ErrorState message={erro} onRetry={carregar} />;
  if (!dados) return <div className="carregando">Carregando…</div>;

  const s = dados.solicitacoes;
  const lembretesAtrasados = dados.lembretes.filter((l) => l.atrasado).length;

  const pedidos = dados.pedidosReuniao ?? [];

  return (
    <div className="dev-painel">
      {pedidos.length > 0 && (
        <button className="dev-alerta dev-alerta-pedido" onClick={() => irPara("agenda")}>
          <IconCalendar size={18} />
          <span>
            <strong>
              {pedidos.length === 1
                ? pedidos[0].solicitante +
                  (pedidos[0].sistema ? " (" + nomeDoSistema(pedidos[0].sistema) + ")" : "") +
                  " pediu uma reunião"
                : pedidos.length + " pedidos de reunião"}
            </strong>{" "}
            {pedidos.length === 1
              ? "para " + dataBr(pedidos[0].data) + (pedidos[0].hora ? " às " + pedidos[0].hora : "") + " — esperando a sua resposta."
              : "esperando a sua resposta."}
          </span>
          <span className="dev-alerta-acao">Responder</span>
        </button>
      )}
      <div className="cards">
        <button className="card dev-card-btn" onClick={() => irPara("solicitacoes")}>
          <span className="card-icon tone-green">
            <IconList size={20} />
          </span>
          <span className="card-body">
            <span className="card-label">Solicitações abertas</span>
            <span className="card-value">{s.abertas}</span>
            <small className="muted">
              {s.urgentes > 0 ? s.urgentes + " urgente(s) · " : ""}
              {s.hoje} chegou(aram) hoje
            </small>
          </span>
        </button>

        <button className="card dev-card-btn" onClick={() => irPara("agenda")}>
          <span className="card-icon tone-teal">
            <IconCalendar size={20} />
          </span>
          <span className="card-body">
            <span className="card-label">Próximas reuniões</span>
            <span className="card-value">{dados.reunioes.length}</span>
            <small className="muted">
              {dados.reunioes[0]
                ? "a próxima em " + dataBr(dados.reunioes[0].data)
                : "nada marcado"}
            </small>
          </span>
        </button>

        <button className="card dev-card-btn" onClick={() => irPara("lembretes")}>
          {/* Âmbar é "olhe isto": sem lembrete nenhum não há o que olhar. */}
          <span
            className={
              "card-icon " +
              (lembretesAtrasados > 0
                ? "tone-red"
                : dados.lembretes.length > 0
                  ? "tone-amber"
                  : "tone-teal")
            }
          >
            <IconClock size={20} />
          </span>
          <span className="card-body">
            <span className="card-label">Lembretes</span>
            <span className="card-value">{dados.lembretes.length}</span>
            <small className="muted">
              {lembretesAtrasados > 0 ? lembretesAtrasados + " atrasado(s)" : "nenhum atrasado"}
              {dados.email.configurado ? " · e-mail ligado" : " · e-mail desligado"}
            </small>
          </span>
        </button>

        <button className="card dev-card-btn" onClick={() => irPara("auditoria")}>
          <span className={"card-icon " + (dados.auditoria.falhas_24h > 5 ? "tone-red" : "tone-lime")}>
            <IconShield size={20} />
          </span>
          <span className="card-body">
            <span className="card-label">Auditoria (24h)</span>
            <span className="card-value">{dados.auditoria.eventos_24h}</span>
            <small className="muted">
              {dados.auditoria.falhas_24h} falha(s) de login · {dados.auditoria.sistemas_24h} sistema(s)
            </small>
          </span>
        </button>
      </div>

      {s.atrasadas > 0 && (
        <div className="dev-alerta">
          <IconAlert size={18} />
          <span>
            <strong>{s.atrasadas}</strong> solicitação(ões) passaram do prazo combinado.
          </span>
          <button className="btn-link" onClick={() => irPara("solicitacoes")}>
            ver
          </button>
        </div>
      )}

      <div className="dev-colunas">
        <section className="dev-bloco">
          <h3 className="section-title">
            <IconList size={15} /> Fila de trabalho
          </h3>
          {dados.fila.length === 0 ? (
            <p className="muted">Nenhuma solicitação em aberto. Dia tranquilo.</p>
          ) : (
            <ul className="dev-lista">
              {dados.fila.map((f) => (
                <li key={f.id} onClick={() => irPara("solicitacoes")}>
                  <span className={"prio prio-" + String(f.prioridade).toLowerCase()}>
                    {f.prioridade}
                  </span>
                  <span className="dev-lista-main">
                    <strong>{f.titulo}</strong>
                    <small className="muted">
                      {nomeDoSistema(f.sistema)} · {f.tipo} · {f.status}
                      {f.solicitante ? " · " + f.solicitante : ""}
                    </small>
                  </span>
                  {f.prazo && <span className="pill pill-neutral">até {dataBr(f.prazo)}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="dev-bloco">
          <h3 className="section-title">
            <IconClock size={15} /> Lembretes e reuniões
          </h3>
          {dados.lembretes.length === 0 && dados.reunioes.length === 0 ? (
            <p className="muted">Nada marcado para os próximos dias.</p>
          ) : (
            <ul className="dev-lista">
              {dados.lembretes.map((l) => (
                <li key={"l" + l.id}>
                  <span
                    className={
                      "pill " + (l.atrasado ? "pill-danger" : l.hoje ? "pill-warn" : "pill-neutral")
                    }
                  >
                    {l.atrasado ? "atrasado" : l.hoje ? "hoje" : dataBr(l.data)}
                  </span>
                  <span className="dev-lista-main">
                    <strong>{l.titulo}</strong>
                    <small className="muted">
                      {l.hora ? l.hora + " · " : ""}
                      {l.email_em ? "e-mail enviado" : "sem e-mail"}
                    </small>
                  </span>
                  <button className="btn-link" onClick={() => concluirLembrete(l.id)}>
                    concluir
                  </button>
                </li>
              ))}
              {dados.reunioes.map((r) => (
                <li key={"r" + r.id}>
                  <span className="pill pill-ok">{dataBr(r.data)}</span>
                  <span className="dev-lista-main">
                    <strong>{r.titulo}</strong>
                    <small className="muted">
                      {r.hora ? r.hora + " · " : ""}
                      {r.local || r.link || "sem local definido"}
                    </small>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="dev-bloco">
        <h3 className="section-title">
          <IconRefresh size={15} /> Últimas atualizações entregues
        </h3>
        {dados.atualizacoes.length === 0 ? (
          <p className="muted">
            Nenhuma atualização registrada ainda. Quando entregar algo, registre em{" "}
            <button className="btn-link" onClick={() => irPara("atualizacoes")}>
              Atualizações
            </button>{" "}
            — dali sai o aviso que aparece dentro dos sistemas.
          </p>
        ) : (
          <ul className="dev-lista">
            {dados.atualizacoes.map((a) => (
              <li key={a.id}>
                <span className="pill pill-neutral">{dataBr(a.data)}</span>
                <span className="dev-lista-main">
                  <strong>{a.titulo}</strong>
                  <small className="muted">
                    {nomeDoSistema(a.sistema)}
                    {a.versao ? " · " + a.versao : ""}
                  </small>
                </span>
                {a.avisado_em && (
                  <span className={"pill " + (a.aviso_ativo ? "pill-ok" : "pill-neutral")}>
                    {a.aviso_ativo ? "avisando" : "aviso encerrado"}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
